import { createApi } from "@reduxjs/toolkit/dist/query/react";
import {
  AuthzListResponse,
  AuthzPolicyDto,
  AuthzRoleDto,
  AuthzUserPoliciesResponse,
  AuthzUserProfileDto,
  CreateAdminWithUserProfileBody,
  CreateAdminWithUserProfileResponse,
  CreateAuthzRolesBody,
  UpdateAuthzUserProfileBody,
} from "../dto/authz.dto";
import { baseQuery } from "./helper";

export interface AuthzMetadataResponse {
  resources: string[];
  actions: string[];
  effects: string[];
  roles: Array<{ id: string; name: string }>;
  policies: Array<{ id: string; resource: string; action: string }>;
  conditionFields: string[];
}

export type AuthzUserProfile = AuthzUserProfileDto;
export type AuthzRole = AuthzRoleDto;
export type AuthzPolicy = AuthzPolicyDto;

export interface AuthzQueryParams {
  search?: string;
  role?: string;
  limit?: number;
  offset?: number;
}

export const authzActionSlice = createApi({
  baseQuery,
  reducerPath: "authzApi",
  tagTypes: ["Authz"],
  endpoints: (build) => ({
    getAuthzMetadata: build.query<AuthzMetadataResponse, void>({
      query: () => ({ url: "/authz/metadata", method: "GET" }),
    }),
    getUserProfiles: build.query<
      AuthzListResponse<AuthzUserProfile>,
      AuthzQueryParams
    >({
      query: (params) => ({
        url: "/authz/user-profiles",
        method: "GET",
        params,
      }),
      providesTags: ["Authz"],
    }),
    updateUserProfile: build.mutation<
      unknown,
      { id: string; body: UpdateAuthzUserProfileBody }
    >({
      query: ({ id, body }) => ({
        url: `/authz/user-profile/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Authz"],
    }),
    deleteUserProfile: build.mutation<unknown, string>({
      query: (id) => ({
        url: `/authz/user-profile/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Authz"],
    }),
    getUserPolicies: build.query<
      AuthzUserPoliciesResponse,
      { adminId: string }
    >({
      query: ({ adminId }) => ({
        url: "/authz/user/policies",
        method: "GET",
        params: { adminId },
      }),
    }),
    getRoles: build.query<AuthzListResponse<AuthzRole>, AuthzQueryParams>({
      query: (params) => ({ url: "/authz/roles", method: "GET", params }),
      providesTags: ["Authz"],
    }),
    createRoles: build.mutation<unknown, CreateAuthzRolesBody>({
      query: (body) => ({ url: "/authz/roles", method: "POST", body }),
      invalidatesTags: ["Authz"],
    }),
    updateRole: build.mutation<
      unknown,
      { id: string; body: Partial<Omit<AuthzRole, "_id">> }
    >({
      query: ({ id, body }) => ({
        url: `/authz/roles/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Authz"],
    }),
    getPolicies: build.query<AuthzListResponse<AuthzPolicy>, AuthzQueryParams>({
      query: (params) => ({ url: "/authz/policies", method: "GET", params }),
      providesTags: ["Authz"],
    }),
    createPolicies: build.mutation<
      unknown,
      { authPolicies: Array<Omit<AuthzPolicy, "_id">> }
    >({
      query: (body) => ({ url: "/authz/policies", method: "POST", body }),
      invalidatesTags: ["Authz"],
    }),
    updatePolicy: build.mutation<
      unknown,
      { id: string; body: Partial<Omit<AuthzPolicy, "_id">> }
    >({
      query: ({ id, body }) => ({
        url: `/authz/policies/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Authz"],
    }),
    createAdminWithUserProfile: build.mutation<
      CreateAdminWithUserProfileResponse,
      CreateAdminWithUserProfileBody
    >({
      query: (body) => ({
        url: "/admin/with-user-profile",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Authz"],
    }),
  }),
});

export const {
  useGetAuthzMetadataQuery,
  useGetUserProfilesQuery,
  useUpdateUserProfileMutation,
  useDeleteUserProfileMutation,
  useGetUserPoliciesQuery,
  useGetRolesQuery,
  useCreateRolesMutation,
  useUpdateRoleMutation,
  useGetPoliciesQuery,
  useCreatePoliciesMutation,
  useUpdatePolicyMutation,
  useCreateAdminWithUserProfileMutation,
} = authzActionSlice;
